import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React from "react";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { useForm, useStore } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { Description, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Listbox, ListboxDescription, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { Divider } from "src/components/base/divider";
import { Button, PrimaryButton } from "src/components/base/button";
import { notify } from "src/components/base/toast";

/**
 * The properties for {@link EphemeralDeviceSettingsView}
 */
export type EphemeralDeviceSettingsViewProps = {};

/** Allowed ephemeral bits, starting at 16, ending at 24 */
const EPHEMERAL_BITS = Array.from({ length: 9 }, (_, i) => 16 + i);

/**
 * Ephemeral device settings view
 */
export default function EphemeralDeviceSettingsView(props: EphemeralDeviceSettingsViewProps) {
    const [t] = useTranslation("admin-settings");

    const navigate = useNavigate();
    const { bit } = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            ephemeral_bit: bit,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                await ApiInternal.settings.setEphemeral({ bit: value.ephemeral_bit });
                await navigate({ replace: true });
                notify.success(t("toast.successfully-updated"));
            },
        },
    });

    const isDirty = useStore(form.store, (store) => store.isDirty);

    return (
        <div className={"flex flex-col justify-start gap-4"}>
            <Form onSubmit={form.handleSubmit} className={"max-w-lg"}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field name={"ephemeral_bit"}>
                            {(fieldApi) => (
                                <Field>
                                    <RequiredLabel>{t("label.ephemeral-bit")}</RequiredLabel>
                                    <Description>{t("description.ephemeral-bit")}</Description>
                                    <Listbox value={fieldApi.state.value} onChange={(e) => fieldApi.handleChange(e)}>
                                        {EPHEMERAL_BITS.map((bit) => (
                                            <ListboxOption value={bit}>
                                                <ListboxLabel>{bit}</ListboxLabel>
                                                <ListboxDescription>
                                                    {t("label.ip-count", { ips: 2 ** (32 - bit) })}
                                                </ListboxDescription>
                                            </ListboxOption>
                                        ))}
                                    </Listbox>
                                </Field>
                            )}
                        </form.Field>

                        <Divider />

                        <div className={"flex justify-end gap-3"}>
                            <Button plain={true} disabled={!isDirty} onClick={() => form.reset()}>
                                {t("button.reset")}
                            </Button>
                            <PrimaryButton type="submit" disabled={!isDirty}>
                                {t("button.save")}
                            </PrimaryButton>
                        </div>
                    </FieldGroup>
                </Fieldset>
            </Form>
        </div>
    );
}

export const Route = createFileRoute("/_admin/a/settings/_settings/ephemeral")({
    component: EphemeralDeviceSettingsView,
    loader: async () => await ApiInternal.settings.getEphemeral(),
});
